import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { normalizeNumericInput } from '@lubehub/validation';
import { useId, } from 'react';
/**
 * Label, control, and message — wired together.
 *
 * The control is a render prop taking the ids it must carry, because the wiring
 * is the part that gets forgotten: a label with no `htmlFor` is a label that
 * does not focus its input, and an error message that is not referenced by
 * `aria-describedby` is never read aloud. Making the ids an argument means a
 * field cannot be assembled wrongly.
 */
export function Field({ label, hint, error, optional = false, children }) {
    const id = useId();
    const messageId = `${id}-message`;
    const message = error ?? hint;
    return (_jsxs("div", { className: "lh-field", children: [_jsxs("label", { className: "lh-field__label", htmlFor: id, children: [label, optional && _jsx("span", { className: "lh-field__optional", children: " (\u0627\u062E\u062A\u064A\u0627\u0631\u064A)" })] }), children({
                id,
                'aria-describedby': message ? messageId : undefined,
                'aria-invalid': error ? true : undefined,
            }), message && (_jsx("span", { id: messageId, className: error ? 'lh-field__error' : 'lh-field__hint', 
                // Announced when it appears, without stealing focus from the input
                // the person is still typing in.
                role: error ? 'alert' : undefined, children: message }))] }));
}
/**
 * True when the control has been marked invalid, however it was marked.
 *
 * `aria-invalid` is typed as a string union as well as a boolean in the DOM,
 * and the string `'false'` is truthy — so a plain truthiness check would paint
 * a valid input red.
 */
function isInvalid(value, explicit) {
    return explicit || value === true || value === 'true';
}
function webNumericMode(type, inputMode, normalize) {
    if (normalize === 'none') {
        return undefined;
    }
    if (normalize !== undefined) {
        return normalize;
    }
    if (type === 'tel' || inputMode === 'tel') {
        return 'phone';
    }
    if (type === 'number' || inputMode === 'decimal') {
        return 'decimal';
    }
    if (inputMode === 'numeric') {
        return 'digits';
    }
    return undefined;
}
export function TextField({ invalid = false, normalize, onChange, type, inputMode, ...rest }) {
    const mode = webNumericMode(type, inputMode, normalize);
    return (_jsx("input", { className: ['lh-input', isInvalid(rest['aria-invalid'], invalid) && 'lh-input--invalid']
            .filter(Boolean)
            .join(' '), type: type, inputMode: inputMode, onChange: (event) => {
            if (mode !== undefined) {
                const next = normalizeNumericInput(event.target.value, mode);
                if (next !== event.target.value) {
                    event.target.value = next;
                }
            }
            onChange?.(event);
        }, ...rest }));
}
export function TextArea({ invalid = false, rows = 4, ...rest }) {
    return (_jsx("textarea", { rows: rows, className: [
            'lh-input',
            'lh-input--textarea',
            isInvalid(rest['aria-invalid'], invalid) && 'lh-input--invalid',
        ]
            .filter(Boolean)
            .join(' '), ...rest }));
}
/**
 * A native `<select>`, styled.
 *
 * Native rather than a custom listbox on purpose: it is keyboard-accessible,
 * screen-reader correct and renders as the platform picker on a phone, none of
 * which a hand-rolled dropdown gets right without a great deal of work. Where
 * search or a long list is needed, that is a different component.
 */
export function Select({ options, placeholder, invalid = false, ...rest }) {
    return (_jsxs("span", { className: "lh-select-wrap", children: [_jsxs("select", { className: [
                    'lh-input',
                    'lh-select',
                    isInvalid(rest['aria-invalid'], invalid) && 'lh-input--invalid',
                ]
                    .filter(Boolean)
                    .join(' '), ...rest, children: [placeholder !== undefined && _jsx("option", { value: "", children: placeholder }), options.map((option) => (_jsx("option", { value: option.value, disabled: option.disabled, children: option.label }, option.value)))] }), _jsx("span", { className: "lh-select-wrap__arrow", "aria-hidden": "true", children: "\u25BE" })] }));
}
export function Checkbox({ label, disabled, ...rest }) {
    return (_jsxs("label", { className: ['lh-checkbox', disabled && 'lh-checkbox--disabled'].filter(Boolean).join(' '), children: [_jsx("input", { type: "checkbox", disabled: disabled, ...rest }), _jsx("span", { className: "lh-text lh-text--body-sm", children: label })] }));
}
/**
 * A settings toggle.
 *
 * A real `<input type="checkbox">` underneath rather than a `div` with
 * `role="switch"`: it is focusable, it toggles with the space bar, it
 * participates in forms, and it works before any JavaScript has loaded.
 */
export function Switch({ label, disabled, ...rest }) {
    return (_jsxs("label", { className: ['lh-switch', disabled && 'lh-checkbox--disabled'].filter(Boolean).join(' '), children: [_jsx("input", { type: "checkbox", role: "switch", disabled: disabled, ...rest }), _jsx("span", { className: "lh-switch__track", "aria-hidden": "true", children: _jsx("span", { className: "lh-switch__thumb" }) }), _jsx("span", { className: "lh-text lh-text--body-sm lh-grow", children: label })] }));
}
//# sourceMappingURL=Field.js.map